import {
  ArrowDownIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from '@cloudclevr/components';
import { useIsDesktop } from '@hooks/utils';
import cn from 'classnames';
import React, { useState } from 'react';
import { Link } from 'react-scroll';
import styles from './styles.module.css';

export type SectionNavItem = {
  id?: string;
  label?: string;
  children?: SectionNavItem[];
};

type SectionNavProps = {
  items: SectionNavItem[];
  headerLabel?: string;
  animationDuration?: number;
  scrollOffset?: number;
  testId?: string;
};

export const SectionNav: React.FC<SectionNavProps> = ({
  items,
  headerLabel = 'Jump to section',
  animationDuration = 300,
  scrollOffset = -140,
  testId = 'section-nav',
}) => {
  const isDesktop = useIsDesktop();
  const [isOpen, setIsOpen] = useState(isDesktop ? true : false);

  // Cast react-scroll's Link to a generic React component to avoid
  // TS2786 when the library's types are incompatible with React types.
  const ScrollLink = Link as unknown as React.ComponentType<any>;

  const toggleOpen = () => {
    !isDesktop ? setIsOpen(!isOpen) : null;
  };

  const closeSectionNav = () => {
    !isDesktop ? setIsOpen(false) : null;
  };

  return (
    <div className={styles.wrapper}>
      <ul className={styles.list}>
        <li
          data-testid={`${testId}-header`}
          onClick={toggleOpen}
          className={cn(
            styles.headerListItem,
            !isDesktop ? 'cursor-pointer' : ''
          )}
        >
          {headerLabel}
          {!isDesktop && (
            <div>
              {!isOpen && <ArrowDownIcon />}
              {isOpen && <ArrowUpIcon />}
            </div>
          )}
        </li>
        {isOpen &&
          items.map((item) => (
            <li
              className={`${styles.listItem} ${
                item.children?.length ? 'flex-col' : 'flex-row'
              }`}
              key={item.id}
              data-testid={`${testId}-item-${item.id}`}
            >
              <ScrollLink
                activeClass="sectionNavActive"
                to={`section${item.id}`}
                spy={true}
                smooth={true}
                offset={scrollOffset}
                duration={animationDuration}
                className={styles.linkItem}
                onClick={closeSectionNav}
              >
                {item.label}
                <ArrowRightIcon
                  color={'text-interfaceColor-80'}
                  size="3"
                  classNames="ml-4"
                />
              </ScrollLink>
              {item.children?.length && (
                <ul className="w-full py-2">
                  {item.children?.map((child) => {
                    return (
                      <li className={styles.listChildItem} key={child.id} data-testid={`${testId}-child-item-${item.id}-${child.id}`}>
                        <ScrollLink
                          activeClass="sectionNavActive"
                          to={`section-${item.id}-${child.id}`}
                          spy={true}
                          smooth={true}
                          offset={scrollOffset}
                          duration={animationDuration}
                          className={styles.linkItem}
                          onClick={closeSectionNav}
                        >
                          {child.label}
                        </ScrollLink>
                        <ArrowRightIcon
                          color={'text-interfaceColor-80'}
                          size="3"
                          classNames="ml-4"
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
};
